import * as React from "react";
import { graphql } from "gatsby";
import { useIntl } from "react-intl";
import Layout from "./layout";
import Hero from "./hero";
import Logo from "./logo";
import Seo from "./seo";

const ZiteEmbed = ({ id }) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(true);
  const [progress, setProgress] = React.useState(0);
  const embedRef = React.useRef(null);

  React.useEffect(() => {
    // Start progress (fake crawl to 90%)
    const timer = setTimeout(() => setProgress(90), 100);

    const src = "https://server.fillout.com/embed/v2-zite/";
    const existingScript = document.querySelector(`script[src="${src}"]`);

    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    document.head.append(script);

    return () => clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    const target = embedRef.current;
    if (!target || isLoaded) {
      return undefined;
    }

    const attachIframeLoader = (iframe) => {
      if (!iframe) {
        return;
      }

      const finishLoading = () => {
        setIsLoaded(true);
        setProgress(100);
      };

      iframe.addEventListener("load", finishLoading, { once: true });
    };

    const currentIframe = target.querySelector("iframe");
    if (currentIframe) {
      attachIframeLoader(currentIframe);
    }

    const observer = new MutationObserver(() => {
      const iframe = target.querySelector("iframe");
      if (iframe) {
        attachIframeLoader(iframe);
        observer.disconnect();
      }
    });

    observer.observe(target, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isLoaded]);

  const handleTransitionEnd = () => {
    if (progress === 100) {
      setIsAnimating(false);
    }
  };

  return (
    <div
      className="embed"
      aria-live="polite"
      aria-atomic="true"
      aria-busy={!isLoaded || isAnimating}
    >
      {(!isLoaded || isAnimating) && (
        <div className="embed-loading" aria-hidden="true">
          <Logo icon />
          <div
            className="embed-loading-bar"
            style={{
              "--embed-loading-width": `${progress}%`,
              "--embed-loading-transition":
                progress === 100
                  ? "width 0.4s ease-out"
                  : "width 15s cubic-bezier(0.1, 0, 0, 1)",
            }}
            onTransitionEnd={handleTransitionEnd}
          />
        </div>
      )}
      <div
        ref={embedRef}
        style={{
          width: "100%",
          height: "700px",
          visibility: isLoaded ? "visible" : "hidden",
        }}
        data-zite-id={id}
        data-zite-embed-type="standard"
        data-zite-inherit-parameters
      />
    </div>
  );
};

export default ZiteEmbed;
